import { create } from "zustand";
import {
  UpdateWorkspace,
  Workspace,
  WorkspacesQuery,
  WorkspacesState,
  CreateWorkspace,
  ListWorkspaces,
} from "../types/workspace";
import { useFetcher } from "./fetcher";
import { Paging } from "../types/paging";
import { Channel, ChannelsQuery, ListChannels } from "../types/channel";
import { sortQueryFromOrderByRecord } from "../utils/query";

export const useWorkspacesStore = create<WorkspacesState>(
  (set, get) =>
    ({
      all: null,
      items: [],
      total: null,
      query: null,
      limit: 20,
      fetchingAll: false,
      search: async (query?: WorkspacesQuery, fetch?: boolean) => {
        set(() => ({ query }));
        if (fetch !== false) {
          await get().fetch(true);
        }
      },
      fetch: async (reset: boolean): Promise<Paging<Workspace> | null> => {
        try {
          const { items, limit, query, total } = get();
          if (!reset && total !== null && total <= items.length ) {
            return null;
          }

          const sort = sortQueryFromOrderByRecord(query?.order_by)
          const params: any = query
            ? { ...query, order_by: undefined, sort, limit, offset: reset ? 0 : items.length }
            : { limit, offset: reset ? 0 : items.length };
          const res = await useFetcher
            .getState()
            .omsFetcher.get<Paging<Workspace>>("workspaces", {
              params,
              paramsSerializer: {
                indexes: null,
              },
            });

          set(() => ({
            items: [...(reset ? [] : items), ...(res?.data?.items ?? [])],
            total: res?.data?.total ?? null,
          }));
          return res?.data ?? null;
        } catch (error) {
          throw error;
        }
      },
      list: async (input?: ListWorkspaces): Promise<Paging<Workspace>> => {
        try {
          const { limit, offset, order_by, ...query } = input
            ? input
            : { limit: 20, offset: 0, order_by: undefined };
          const sortQuery = sortQueryFromOrderByRecord(order_by);
          const params: any = query
            ? {
                ...query,
                limit: limit ? limit : 20,
                offset: offset ? offset : 0,
                sort: sortQuery,
              }
            : {
                limit: limit ? limit : 20,
                offset: offset ? offset : 0,
                sort: sortQuery,
              };
          const res = await useFetcher
            .getState()
            .omsFetcher.get<Paging<Workspace>>("workspaces", {
              params,
              paramsSerializer: {
                indexes: null,
              },
            });
          return res?.data ?? null;
        } catch (error) {
          throw error;
        }
      },
      get: async (id: number): Promise<Workspace | null> => {
        try {
          const res = await useFetcher
            .getState()
            .omsFetcher.get<Workspace>(`workspaces/${id}`);
          return res?.data ?? null;
        } catch (error) {
          throw error;
        }
      },
      create: async (input: CreateWorkspace): Promise<Workspace | null> => {
        try {
          const res = await useFetcher
            .getState()
            .omsFetcher.post<Workspace>(`workspaces`, input);
          get().clear();
          return res?.data ?? null;
        } catch (error) {
          throw error;
        }
      },
      update: async (
        id: number,
        input: UpdateWorkspace
      ): Promise<Workspace | null> => {
        try {
          const res = await useFetcher
            .getState()
            .omsFetcher.patch<Workspace>(`workspaces/${id}`, input);
          if (res.data) {
            const updatedWorkspace = res.data;
            set((state) => ({
              items: state.items.map((item) =>
                item.id === updatedWorkspace.id ? { ...item, ...updatedWorkspace } : item
              ),
            }));
          }
          return res.data ?? null;
        } catch (error) {
          throw error;
        }
      },
      delete: async (id: number): Promise<void> => {
        try {
          const res = await useFetcher
            .getState()
            .omsFetcher.delete(`workspaces/${id}`);
          get().clear();
        } catch (error) {
          throw error;
        }
      },
      findChannels: async (
        id: number,
        input?: ListChannels
      ): Promise<Channel[]> => {
        try {
          const { limit, offset, ...query } = input
            ? input
            : { limit: 20, offset: 0 };
          const params: any = query
            ? {
                ...query,
                limit: limit ? limit : 20,
                offset: offset ? offset : 0,
              }
            : { limit: limit ? limit : 20, offset: offset ? offset : 0 };
          const res = await useFetcher
            .getState()
            .omsFetcher.get(`workspaces/${id}/channels`, { params });
          return res?.data ?? null;
        } catch (error) {
          throw error;
        }
      },
      getChannels: async (id: number): Promise<Channel[]> => {
        try {
          const items = [] as Channel[];
          const limit = 100;
          let hasMore = true;
          while (hasMore) {
            const offset = items.length;
            const params = {
              limit,
              offset,
              sort: "name",
            };
            const res = await useFetcher
              .getState()
              .omsFetcher.get<Paging<Channel>>(`workspaces/${id}/channels`, {
                params,
              });
            if (res?.data?.items && res.data.items.length > 0) {
              items.push(...res.data.items);
              hasMore = items.length < res.data.total;
            } else {
              break;
            }
          }
          return items;
        } catch (error) {
          throw error;
        }
      },
      setChannels: async (id: number, channels: Channel[]): Promise<void> => {
        try {
          await useFetcher.getState().omsFetcher.put(`workspaces/${id}/channels`, {
            channel_ids: channels.map((ch: Channel) => ch.id),
          });
        } catch (error) {
          throw error;
        }
      },
      clear: () => {
        set(() => ({
          items: [],
          total: null,
          query: null,
          all: null,
          fetchingAll: false,
        }));
      },
      fetchAll: async (reset: boolean): Promise<Workspace[]> => {
        if (!reset && get().all !== null) {
          return get().all!;
        }
        try {
          set(() => ({ fetchingAll: true }));
          const all = [] as Workspace[];
          while (true) {
            const res = await useFetcher
              .getState()
              .omsFetcher.get<Paging<Workspace>>("workspaces", {
                params: {
                  limit: 100,
                  offset: all.length,
                  sort: "name",
                },
              });
            if (res?.data?.items && res.data.items.length > 0) {
              all.push(...res.data.items);
              if (all.length >= res.data.total) {
                break;
              }
            } else {
              break;
            }
          }
          set(() => ({ all }));
          return all;
        } catch (err) {
          throw err;
        } finally {
          set(() => ({ fetchingAll: false }));
        }
      },
    } as WorkspacesState)
);
